import {ThemeProvider} from '@mui/material/styles'
import type {FC, ReactNode} from 'react'
import {useEffect} from 'react'
import {useDispatch, useSelector} from 'react-redux'
import {darkTheme} from './darkTheme'
import {lightTheme} from './lightTheme'
import type {RootState} from '@containers/App/store'
import {setPreferredTheme} from '@redux/slices/user'

// Shared with the new experience (src/v2/core/preferences/appearance.ts): after
// either side writes `mbai-preferred-theme`, it fires this; the other re-reads.
const PREFERENCES_CHANGED_EVENT = 'mbai:preferences-changed'

const MBAIThemeProvider: FC<{children: ReactNode}> = ({children}): JSX.Element => {
  const theme = useSelector((state: RootState) => state.user.preferredTheme)
  const currentTheme = theme === 'dark' ? darkTheme : lightTheme

  // See index.css: Bootstrap's body background reset can win the cascade
  // over CssBaseline's depending on entry load order (AB#6141).
  useEffect(() => {
    document.documentElement.setAttribute('data-mbai-theme', theme === 'dark' ? 'dark' : 'light')
    // The new experience follows (e.g. the profile's theme loaded at sign-in).
    window.dispatchEvent(new Event(PREFERENCES_CHANGED_EVENT))
  }, [theme])

  const dispatch = useDispatch()
  useEffect(() => {
    const follow = (): void => {
      const stored = localStorage.getItem('mbai-preferred-theme')
      if (stored && stored !== theme) dispatch(setPreferredTheme(stored))
    }
    window.addEventListener(PREFERENCES_CHANGED_EVENT, follow)
    return () => window.removeEventListener(PREFERENCES_CHANGED_EVENT, follow)
  }, [dispatch, theme])

  return <ThemeProvider theme={currentTheme}>{children}</ThemeProvider>
}

export default MBAIThemeProvider
